import { useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Quote, Linkedin, ChevronLeft, ChevronRight } from 'lucide-react';
import { useAutoAdvance } from '@/lib/useAutoAdvance';

// "What teams say" — verbatim LinkedIn recommendations from colleagues and
// managers Himanshu has worked with. Avatars are their LinkedIn profile
// photos. The header link points to the public recommendations page.

const RECOMMENDATIONS_URL =
    'https://www.linkedin.com/in/anshuu/details/recommendations/';

const testimonials = [
    {
        id: 'sakshi-kumari',
        quote:
            'What stands out the most about him is not just his exceptional UX design skills but also his incredible speed and efficiency. He gets things done fast without sacrificing quality.',
        author: 'Sakshi Kumari',
        role: 'Product Designer · NIFT Gold Medalist',
        highlight: 'Worked together · Wipro & Engagedly',
        date: 'Dec 2024',
        avatar: '/avatars/rec-sakshi.webp',
    },
    {
        id: 'nishant-nandardhane',
        quote:
            'Himanshu possesses a rare combination of technical prowess, design thinking, and a deep understanding of user needs. He consistently delivered high-quality design solutions that were both visually appealing and intuitive.',
        author: 'Nishant D. Nandardhane',
        role: 'User Interface Designer',
        highlight: 'Collaborated across teams',
        date: 'Feb 2024',
        avatar: '/avatars/rec-nishant.webp',
    },
    {
        id: 'kunal-gandhi',
        quote:
            'I find him a great learner — his ability to visualise and redefine the design is excellent. He goes beyond the expectations of the work and makes sure it is completed on time and of good quality.',
        author: 'Kunal Gandhi',
        role: 'Software Architect · ZEASN',
        highlight: 'Managed Himanshu directly',
        date: 'Jul 2020',
        avatar: '/avatars/rec-kunal.webp',
    },
    {
        id: 'brahmdev-kumar',
        quote:
            'He is a rare designer who provides a design solution to solve a business problem rather than just reskinning the UI. With the right level of motivation and freedom, he can do wonders.',
        author: 'Brahmdev Kumar',
        role: 'Manager · Solution Architect (Ex-IQVIA)',
        highlight: 'Worked on the same team',
        date: 'Jun 2020',
        avatar: '/avatars/rec-brahmdev.webp',
    },
];

const Testimonials = () => {
    const [index, setIndex] = useState(0);
    const total = testimonials.length;
    const t = testimonials[index];
    const hoverRef = useRef(null);

    const go = (dir) => setIndex((p) => (p + dir + total) % total);

    useAutoAdvance({
        interval: 6000,
        onTick: () => setIndex((p) => (p + 1) % total),
        hoverRef,
    });

    return (
        <div
            ref={hoverRef}
            data-testid="testimonials-widget"
            className="relative flex flex-col h-full"
        >
            {/* Head — meta row aligned to the top */}
            <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
                <div className="flex items-center gap-3 font-mono-brand text-[10.5px] uppercase tracking-[0.24em] text-[var(--ink-3)]">
                    <Quote size={12} className="text-[var(--accent)]" />
                    <span>What teams say</span>
                    <span className="w-8 h-[1px] bg-[var(--line-2)]" />
                    <span className="tabular-nums text-[var(--ink-2)]">
                        {String(index + 1).padStart(2, '0')} / {String(total).padStart(2, '0')}
                    </span>
                </div>
                <div className="flex items-center gap-2">
                    <a
                        href={RECOMMENDATIONS_URL}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="testimonials-linkedin-link"
                        data-cursor="hover"
                        className="hidden sm:inline-block font-mono-brand text-[10px] uppercase tracking-[0.24em] text-[var(--ink-2)] hover:text-[var(--accent)] transition-colors"
                    >
                        Full recommendations on LinkedIn ↗
                    </a>
                    <div className="flex items-center gap-1">
                        <button
                            type="button"
                            onClick={() => go(-1)}
                            data-testid="testimonial-prev"
                            data-cursor="hover"
                            aria-label="Previous testimonial"
                            className="inline-flex items-center justify-center w-8 h-8 rounded-full text-[var(--ink-2)] hover:text-[var(--accent)] transition-colors"
                        >
                            <ChevronLeft size={16} />
                        </button>
                        <button
                            type="button"
                            onClick={() => go(1)}
                            data-testid="testimonial-next"
                            data-cursor="hover"
                            aria-label="Next testimonial"
                            className="inline-flex items-center justify-center w-8 h-8 rounded-full text-[var(--ink)] hover:text-[var(--accent)] transition-colors"
                        >
                            <ChevronRight size={16} />
                        </button>
                    </div>
                </div>
            </div>

            {/* Oversized quote — reads as a magazine pull */}
            <div className="relative flex-1 flex flex-col">
                <AnimatePresence mode="wait">
                    <motion.div
                        key={t.id}
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        transition={{ duration: 0.45, ease: [0.2, 0.8, 0.2, 1] }}
                        data-testid={`testimonial-card-${index}`}
                        className="flex-1 flex flex-col"
                    >
                        <blockquote className="font-display-italic text-[26px] md:text-[30px] lg:text-[32px] leading-[1.18] text-[var(--ink)]">
                            <span className="text-[var(--accent)] mr-1">“</span>
                            {t.quote}
                            <span className="text-[var(--accent)] ml-0.5">”</span>
                        </blockquote>

                        {/* Fine hairline separator instead of a box border */}
                        <div className="mt-8 mb-4 flex items-center gap-3">
                            <span className="w-8 h-[1px] bg-[var(--accent)]" />
                            <span className="font-mono-brand text-[10px] uppercase tracking-[0.24em] text-[var(--accent)]">
                                {t.highlight}
                            </span>
                        </div>

                        <div className="flex items-center gap-3">
                            <span className="relative inline-flex shrink-0">
                                <img
                                    src={t.avatar}
                                    alt={t.author}
                                    width={40}
                                    height={40}
                                    loading={index === 0 ? 'eager' : 'lazy'}
                                    decoding="async"
                                    className="w-10 h-10 rounded-full object-cover border border-[var(--line-2)]"
                                />
                                <span
                                    className="absolute -bottom-0.5 -right-0.5 inline-flex items-center justify-center w-4 h-4 rounded-full bg-[var(--accent)] text-[var(--accent-ink)] ring-2 ring-[var(--surface)]"
                                    aria-label="Sourced from LinkedIn"
                                >
                                    <Linkedin size={9} />
                                </span>
                            </span>
                            <div>
                                <div className="font-sans-brand text-[14px] text-[var(--ink)] font-medium leading-tight">
                                    {t.author}
                                </div>
                                <div className="mt-0.5 font-mono-brand text-[10px] uppercase tracking-[0.2em] text-[var(--ink-3)]">
                                    {t.role}
                                </div>
                            </div>
                            <span className="ml-auto font-mono-brand text-[10px] uppercase tracking-[0.2em] text-[var(--ink-3)] tabular-nums self-start">
                                {t.date}
                            </span>
                        </div>
                    </motion.div>
                </AnimatePresence>

                {/* Dots */}
                <div className="mt-6 flex items-center gap-1.5">
                    {testimonials.map((tt, i) => (
                        <button
                            key={tt.id}
                            type="button"
                            onClick={() => setIndex(i)}
                            data-testid={`testimonial-dot-${i}`}
                            data-cursor="hover"
                            aria-label={`Show testimonial ${i + 1}`}
                            className={`h-[3px] rounded-full transition-all duration-500 ${
                                i === index
                                    ? 'w-8 bg-[var(--accent)]'
                                    : 'w-4 bg-[var(--line-2)] hover:bg-[var(--ink-3)]'
                            }`}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
};

export default Testimonials;
